import { useEffect } from 'react'
import { Modal, Form, Input, InputNumber, Button, message } from 'antd'
import { UserOutlined, MailOutlined, BankOutlined, TeamOutlined } from '@ant-design/icons'
import i18n from 'i18next'

import { useContactService, ContactType } from '@/api/use-service'

export type { ContactType }
export type AddonService = 'pentesting' | 'training' | 'compliance'

interface ContactModalProps {
  open: boolean
  onClose: () => void
  type: ContactType
  user?: { email?: string; name?: string }
  addonService?: AddonService
}

const addonServiceKey = (service: AddonService) => `contact_modal.addonService_${service}`

export const ContactModal = ({ open, onClose, type, user, addonService }: ContactModalProps) => {
  const [form] = Form.useForm()
  const t = (key: string) => i18n.t(`contact_modal.${key}`)
  const {
    createContactRequest: { invoke, isLoading },
  } = useContactService()
  const isLoggedIn = !!user?.email

  useEffect(() => {
    if (!open) form.resetFields()
  }, [open, form])

  const handleSubmit = async (values: {
    email?: string
    name?: string
    companyName?: string
    employeeCount?: number
    message?: string
  }) => {
    const payload = {
      email: (user?.email || values.email) as string,
      name: user?.name || values.name,
      message: values.message,
      type,
      ...(addonService && { addonService: i18n.t(addonServiceKey(addonService)) }),
      ...(values.companyName && { companyName: values.companyName }),
      ...(values.employeeCount && { employeeCount: values.employeeCount }),
    }

    const { error } = await invoke({ payload })

    if (error) {
      message.error(t('form_error'))
      return
    }

    message.success(t(`${type}_success`))
    form.resetFields()
    onClose()
  }

  const modalTitle =
    type === 'addon' && addonService
      ? `${t('addon_title')}: ${i18n.t(addonServiceKey(addonService))}`
      : t(`${type}_title`)

  return (
    <Modal title={modalTitle} open={open} onCancel={onClose} footer={null} width={480}>
      <p className="mb-4 text-gray-500">{t(`${type}_description`)}</p>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        {!isLoggedIn && (
          <>
            <Form.Item
              name="email"
              label={t('form_email')}
              rules={[
                { required: true, message: t('form_emailRequired') },
                { type: 'email', message: t('form_emailInvalid') },
              ]}
            >
              <Input prefix={<MailOutlined />} placeholder="you@company.com" />
            </Form.Item>

            <Form.Item name="name" label={t('form_name')}>
              <Input prefix={<UserOutlined />} placeholder={t('form_namePlaceholder')} />
            </Form.Item>
          </>
        )}

        {(type === 'addon' || type === 'partner') && !isLoggedIn && (
          <Form.Item
            name="companyName"
            label={t('form_companyName')}
            rules={[{ required: true, message: t('form_companyNameRequired') }]}
          >
            <Input prefix={<BankOutlined />} placeholder={t('form_companyNamePlaceholder')} />
          </Form.Item>
        )}

        {type === 'addon' && !isLoggedIn && (
          <Form.Item
            name="employeeCount"
            label={t('form_employeeCount')}
            rules={[{ required: true, message: t('form_employeeCountRequired') }]}
          >
            <InputNumber
              prefix={<TeamOutlined />}
              placeholder={t('form_employeeCountPlaceholder')}
              min={1}
              max={100000}
              className="w-full"
            />
          </Form.Item>
        )}

        <Form.Item name="message" label={t('form_message')}>
          <Input.TextArea rows={4} placeholder={t(`${type}_placeholder`)} />
        </Form.Item>

        <Form.Item className="mb-0">
          <Button type="primary" htmlType="submit" loading={isLoading} block size="large">
            {t('form_submit')}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  )
}

// Backward compatibility alias
export const ContactSalesModal = ({ open, onClose }: { open: boolean; onClose: () => void }) => (
  <ContactModal open={open} onClose={onClose} type="enterprise" />
)
